import type { Sfx, SfxName } from "./sfx";

/** One placed ambience zone out of a map's `placements.json`: the Ambience
 *  property record (`MapType.cpp:216-262`) at the position, range and
 *  full-volume share its `areaambiencedata.txt` object gives it
 *  (`Area.cpp:992-1069,665-693`). */
export interface AmbienceZone {
  readonly name: string;
  readonly playType: string;
  readonly sounds: readonly string[];
  readonly position: { readonly xMm: number; readonly yMm: number; readonly zMm: number };
  readonly rangeMm: number;
  readonly maxVolumeAreaPercentage: number;
}

/** What a zone asks of the sound layer: one looping voice per zone index. */
export interface AmbienceLoops {
  start(zone: number, sound: string): void;
  setVolume(zone: number, volume: number): void;
  stop(zone: number): void;
}

/** `TAmbienceInstance::__GetVolumeFromDistance` (`Area.cpp:1399-1408`): full
 *  volume inside `range * MaxVolumeAreaPercentage`, then linear to nothing at
 *  the range; full everywhere when that share is not positive. */
export function volumeFromDistance(distanceMm: number, rangeMm: number, share: number): number {
  const fullMm = rangeMm * share;
  if (fullMm <= 0) return 1;
  if (distanceMm <= fullMm) return 1;
  return 1 - (distanceMm - fullMm) / (rangeMm - fullMm);
}

/** The map's ambience zones, updated from the listener each frame the way
 *  `CArea::UpdateAroundAmbience` updates every instance (`Area.cpp:103-115`,
 *  `MapOutdoorUpdate.cpp:202-211`, `MapManager.cpp:174-182`). Only `LOOP` is
 *  played: every zone the 2014 pack places is `LOOP`, so `UpdateOnceSound` and
 *  `UpdateStepSound` are never selected (`Area.cpp:677-692`). */
export class AmbienceZones {
  private readonly playing = new Set<number>();

  constructor(private readonly zones: readonly AmbienceZone[]) {}

  update(listener: { xMm: number; yMm: number; zMm: number }, loops: AmbienceLoops): void {
    this.zones.forEach((zone, index) => {
      // `__Update` returns for a zone with no range (`Area.cpp:1311-1317`),
      // and no other play type has an update to call.
      if (zone.rangeMm === 0 || zone.playType !== "LOOP") return;
      const distanceMm = Math.hypot(
        zone.position.xMm - listener.xMm,
        zone.position.yMm - listener.yMm,
        zone.position.zMm - listener.zMm,
      );
      // `UpdateLoopSound` (`Area.cpp:1367-1397`): the first sound starts once on
      // entering, its volume follows the distance while inside, and it stops
      // on leaving. `DWORD(fDistance) < dwRange`: the range edge is outside.
      if (Math.trunc(distanceMm / 10) < Math.trunc(zone.rangeMm / 10)) {
        if (!this.playing.has(index)) {
          if (zone.sounds.length === 0) return;
          loops.start(index, zone.sounds[0]);
          this.playing.add(index);
        }
        loops.setVolume(index, volumeFromDistance(distanceMm, zone.rangeMm, zone.maxVolumeAreaPercentage));
      } else if (this.playing.delete(index)) {
        loops.stop(index);
      }
    });
  }

  /** The map is going: stop every zone still playing. */
  clear(loops: AmbienceLoops): void {
    for (const index of this.playing) loops.stop(index);
    this.playing.clear();
  }
}

/** The clip `tools/build_sfx.sh` built for a zone's sound, by the file name the
 *  record gives, or null for one it did not build. */
export function ambienceClipFor(sound: string): SfxName | null {
  const file = sound.toLowerCase().replace(/\\/g, "/").split("/").pop();
  if (file === "waterfall.wav") return "ambience_waterfall";
  if (file === "warp_test.mp3") return "ambience_warp";
  return null;
}

/** The zones a map's `placements.json` carries, or none where the file has no
 *  `ambiences` array or cannot be fetched. */
export async function loadAmbienceZones(
  url: string,
  fetchFn: typeof fetch = fetch,
): Promise<readonly AmbienceZone[]> {
  try {
    const response = await fetchFn(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { ambiences?: readonly AmbienceZone[] };
    return Array.isArray(body.ambiences) ? body.ambiences : [];
  } catch (error) {
    console.warn(`ambience zones unavailable (${String(error)})`);
    return [];
  }
}

/** `AmbienceLoops` over the sound effects player: one looping voice per zone. */
export class SfxAmbienceLoops implements AmbienceLoops {
  private readonly voices = new Map<number, { setVolume(volume: number): void; stop(): void }>();

  constructor(private readonly sfx: Pick<Sfx, "loop">) {}

  start(zone: number, sound: string): void {
    const clip = ambienceClipFor(sound);
    if (clip === null) return;
    const voice = this.sfx.loop(clip, 1);
    if (voice !== null) this.voices.set(zone, voice);
  }

  setVolume(zone: number, volume: number): void {
    this.voices.get(zone)?.setVolume(volume);
  }

  stop(zone: number): void {
    this.voices.get(zone)?.stop();
    this.voices.delete(zone);
  }
}
